import { useEffect, useState } from "react";

/**
 * Gate for lazy-mounting closed-at-paint overlay chrome (auth drawer, mobile
 * nav drawer, add-to-cart modal UI, TPA welcome modal, search autocomplete).
 *
 * Why (COM-189, wave 4): the layout route chunk is modulepreloaded on every
 * document, so everything it imports streams AHEAD of the hero image on the
 * initial load — PR #1000's measurement showed the hero's ~4.0s LCP Load Delay
 * is pure bandwidth contention with that JS, not discovery. Every overlay this
 * gate defers renders NOTHING during SSR (portal content behind a closed
 * dialog/drawer), so moving its module graph into a lazy chunk that loads
 * after hydration cannot change the SSR document — it only moves bytes out of
 * the critical request wave.
 *
 * Contract:
 *  - Returns false during SSR and on the hydration render (so the client's
 *    first render matches the server, which never renders these subtrees).
 *  - Flips true on the FIRST of: `openNow()` returning true at effect time
 *    (e.g. a `#login` hash already in the URL), the first user interaction
 *    (pointerdown/keydown — an interaction can need an overlay one event
 *    later, so start fetching on the earliest signal), or an idle callback
 *    (~immediately after load on an idle main thread, capped at 2.5s so a
 *    busy trace can't starve it; plain timeout on Safari, which has no
 *    requestIdleCallback).
 *  - Once true it stays true — overlays mount once and keep their state.
 *
 * NOT for SSR-visible chrome: anything the server paints (header, footer,
 * triggers) must stay in the synchronous graph. See COM-186 for what a
 * deferral gate around SSR-visible content does (blank + CLS); this hook
 * exists precisely to avoid that class of change by only gating subtrees
 * whose SSR output is nothing.
 */
export function useOverlaysReady(options?: {
  /**
   * Checked once, client-side, before any waiting: return true to mount the
   * overlays immediately (e.g. the URL carries an auth-drawer hash a shopper
   * followed from an email).
   */
  openNow?: () => boolean;
}): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;
    if (options?.openNow?.()) {
      setReady(true);
      return;
    }

    const arm = () => setReady(true);
    const listenerOpts = { once: true, passive: true, capture: true } as const;
    window.addEventListener("pointerdown", arm, listenerOpts);
    window.addEventListener("keydown", arm, listenerOpts);
    // Wave 4B: several overlays open on HOVER (minicart, account flyout), so
    // the first mouse movement over the page is already intent enough to start
    // fetching — well before any pointerdown. Lab traces send no input, so
    // Lighthouse still sees the fully deferred path.
    window.addEventListener("mouseover", arm, listenerOpts);

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(arm, { timeout: 2500 });
    } else {
      // Safari: no requestIdleCallback. A short post-hydration delay keeps the
      // chunks out of the initial request wave, which is the whole point.
      timeoutId = setTimeout(arm, 1500);
    }

    return () => {
      window.removeEventListener("pointerdown", arm, listenerOpts);
      window.removeEventListener("keydown", arm, listenerOpts);
      window.removeEventListener("mouseover", arm, listenerOpts);
      if (idleId !== undefined) window.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
    // `openNow` is deliberately read once per arming pass, not re-subscribed
    // per identity — callers pass inline closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  return ready;
}
